import Big from 'big.js'

import type {
  CatalogueBundledMemberV2,
  ResolvedCatalogue,
  ResolvedExtra,
  ResolvedOption,
  ResolvedShellProduct,
  ResolvedVariant
} from 'models/purchaseFlowRevamp'

import type { PackMemberAddons } from '../../store'
import type { PackMemberLineAddons } from '../../store/packLines'
import { addonLists } from './useDrawerAddons'
import { computeDrawerPricing } from './useDrawerPricing'

export interface PackMemberQuote {
  member: CatalogueBundledMemberV2
  shell: ResolvedShellProduct
  variant: ResolvedVariant
  currency: string | null
  /** The member's own price, as the backend resolved it for this session. */
  unitPrice: Big | null
  /** The member with its chosen add-ons. */
  total: Big | null
  /** The member's options, offered inside its row; products sold beside it are not. */
  options: ResolvedOption[]
  extras: ResolvedExtra[]
  /** The chosen add-ons narrowed to those offered, as the member's cart line carries them. */
  lineAddons: PackMemberLineAddons
}

/** Prices one pack member with the add-ons chosen on it, or null when the catalogue does not serve it. */
export function quotePackMember(
  catalogue: ResolvedCatalogue | undefined,
  member: CatalogueBundledMemberV2,
  addons: PackMemberAddons
): PackMemberQuote | null {
  const shell = catalogue?.shellProducts.find((candidate) => candidate.variants.some((v) => v.id === member.productCode))
  const variant = shell?.variants.find((candidate) => candidate.id === member.productCode)
  if (!shell || !variant) return null

  const lists = addonLists(variant.options, variant.extras)
  const optionIds = new Set(lists.productOptions.map((option) => option.id))
  const extraIds = new Set(lists.productExtras.map((extra) => extra.id))
  const optionValues = Object.fromEntries(Object.entries(addons.optionValues).filter(([id]) => optionIds.has(id)))
  const extraServiceIds = addons.extraIds.filter((id) => extraIds.has(id))
  const extraQuantities = Object.fromEntries(Object.entries(addons.extraQuantities).filter(([id]) => extraIds.has(id)))

  const pricing = computeDrawerPricing(catalogue, shell.id, variant.id, optionValues, extraServiceIds, 1, extraQuantities)
  const unitPrice = member.price ? new Big(member.price.amount) : pricing.unitPrice
  const addonsPrice = pricing.addonsPrice ?? new Big(0)

  return {
    member,
    shell,
    variant,
    currency: member.price?.currency ?? pricing.currency,
    unitPrice,
    total: unitPrice ? unitPrice.plus(addonsPrice) : null,
    options: lists.productOptions,
    extras: lists.productExtras,
    lineAddons: {
      optionValues,
      extraServiceIds,
      extraQuantities,
      addonsPrice: addonsPrice.gt(0) ? addonsPrice.toFixed(2) : null,
    },
  }
}
